import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { ContentReportService } from '../../../services/content-report.service';

export interface ReportDialogData {
  postId?: number;
  commentId?: number;
}

export interface ReportDialogResult {
  reported: boolean;
  hidePost: boolean;
  hideComment: boolean;
}

const REASON_LABELS: Record<string, string> = {
  inappropriate_content: 'Inappropriate content',
  harassment_or_bullying: 'Harassment or bullying',
  spam: 'Spam',
  misinformation: 'Misinformation',
  hate_speech: 'Hate speech',
  other: 'Other',
};

/**
 * Dialog for reporting a post. After successful report submission,
 * offers the user the option to also hide the post from their feed.
 */
@Component({
  selector: 'app-report-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatRadioModule,
    MatInputModule,
    MatIconModule,
  ],
  templateUrl: './report-dialog.html',
  styleUrl: './report-dialog.css',
})
export class ReportDialog implements OnInit {
  private dialogRef = inject(MatDialogRef<ReportDialog>);
  private data: ReportDialogData = inject(MAT_DIALOG_DATA);
  private reportService = inject(ContentReportService);
  private cdr = inject(ChangeDetectorRef);

  reasons: string[] = [];
  reasonLabels = REASON_LABELS;
  selectedReason = '';
  details = '';
  submitting = false;
  error = '';

  /** Whether the report was successfully submitted — switches to the hide prompt view. */
  reportSubmitted = false;

  /** Whether we're reporting a comment or a post. */
  get isComment(): boolean {
    return !!this.data.commentId;
  }

  get contentLabel(): string {
    return this.isComment ? 'comment' : 'post';
  }

  ngOnInit(): void {
    this.reportService.getReasons().subscribe({
      next: (reasons) => {
        this.reasons = reasons;
        this.cdr.detectChanges();
      },
      error: () => {
        this.reasons = Object.keys(REASON_LABELS);
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Returns a human-readable label for a reason key.
   */
  getLabel(reason: string): string {
    return REASON_LABELS[reason] || reason;
  }

  /**
   * Submits the report. On success, shows the hide prompt instead of closing.
   */
  submit(): void {
    if (!this.selectedReason) return;

    this.submitting = true;
    this.error = '';

    const report$ = this.isComment
      ? this.reportService.reportComment(this.data.commentId!, this.selectedReason, this.details)
      : this.reportService.reportPost(this.data.postId!, this.selectedReason, this.details);

    report$.subscribe({
      next: () => {
        this.submitting = false;
        this.reportSubmitted = true;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.submitting = false;
        this.error = err.error?.error || 'Failed to submit report. Please try again.';
        this.cdr.markForCheck();
      },
    });
  }

  /** User chose to also hide the content after reporting. */
  hidePost(): void {
    if (this.isComment) {
      this.dialogRef.close({
        reported: true,
        hidePost: false,
        hideComment: true,
      } as ReportDialogResult);
    } else {
      this.dialogRef.close({
        reported: true,
        hidePost: true,
        hideComment: false,
      } as ReportDialogResult);
    }
  }

  /** User reported but chose not to hide the content. */
  doneWithoutHiding(): void {
    this.dialogRef.close({
      reported: true,
      hidePost: false,
      hideComment: false,
    } as ReportDialogResult);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
